import {
  refinementList,
  menu,
  searchBox,
  clearRefinements,
  stats,
  sortBy,
} from 'instantsearch.js/es/widgets';

import { connectMenu } from 'instantsearch.js/es/connectors';

import { createPanel } from './createPanel.js';
import {
  itemTemplate,
  showMoreTemplate,
  commonClasses,
  statsTemplate,
  clearRefinementsTemplate,
  texts,
} from './common.js';
import { algoliaLog } from '../config/algolia.js';
import { createFilteredQueryHook } from './queryFilter.js';

/**
 * Known values that should always be visible (injected at count 0 if missing).
 * Only use for facets with a fixed, known set of values.
 */
const knownValues = {
  skillLevel: ['Beginner', 'Intermediate', 'Advanced'],
};

const sortOrders = {
  skillLevel: ['Beginner', 'Intermediate', 'Advanced'],
};

function sortItems(attribute) {
  const customOrder = sortOrders[attribute];
  const known = knownValues[attribute];

  return (items) => {
    let result = [...items];

    // Only inject for hardcoded known values (e.g. Skill Level)
    if (known) {
      const existing = new Set(result.map((i) => i.label));
      known.forEach((label) => {
        if (!existing.has(label)) {
          result.push({
            label,
            value: label,
            count: 0,
            isRefined: false,
            highlighted: label,
          });
        }
      });
    }

    // Sort
    if (customOrder) {
      const orderMap = Object.fromEntries(customOrder.map((v, i) => [v, i]));
      result.sort((a, b) => {
        const aIdx = orderMap[a.label] ?? 999;
        const bIdx = orderMap[b.label] ?? 999;
        return aIdx - bIdx;
      });
    } else {
      result.sort((a, b) => a.label.localeCompare(b.label));
    }

    return result;
  };
}

const widgetConfigs = {
  refinementList: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;
    const sorter = sortItems(attribute);

    const widgetOptions = {
      templates: {
        item: (item) => {
          const disabledClass =
            item.count === 0 && !item.isRefined
              ? 'opacity-40 pointer-events-none'
              : '';
          return `<div class="${disabledClass}">${itemTemplate(item)}</div>`;
        },
        showMoreText: showMoreTemplate,
      },
      transformItems: sorter,
      cssClasses: {
        root: '',
        list: commonClasses.list.join(' '),
        item: commonClasses.item.join(' '),
        selectedItem: commonClasses.selectedItem.join(' '),
        showMore: commonClasses.showMore.join(' '),
        noRefinementRoot: commonClasses.noRefinementRoot.join(' '),
        searchBox: 'mb-3',
      },
      ...options,
    };

    if (usePanel) {
      return createPanel(refinementList, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return refinementList({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  menu: (container, attribute, options) => {
    const usePanel = container.dataset.panel !== 'false';
    const label = container.dataset.label || attribute;
    const isInline = container.dataset.inline === 'true';
    const showAll = container.dataset.showAll === 'true';
    const allLabel = container.dataset.allLabel || texts.all;

    if (isInline) {
      let cachedWrapper = null;
      let currentRefine = null;

      const renderMenuTabs = (renderOptions, isFirstRender) => {
        const { items, refine } = renderOptions;
        currentRefine = refine;
        const containerEl =
          typeof container === 'string'
            ? document.querySelector(container)
            : container;

        if (isFirstRender) {
          const wrapper = document.createElement('div');
          wrapper.className = 'filter-tabs';
          wrapper.addEventListener('click', (e) => {
            const btn = e.target.closest('button');
            if (btn && btn.dataset.value !== undefined && currentRefine) {
              currentRefine(btn.dataset.value);
            }
          });
          containerEl.appendChild(wrapper);
          cachedWrapper = wrapper;
        }

        const wrapper = cachedWrapper || containerEl.querySelector('div');
        if (!wrapper) return;

        const hasRefinement = items.some((item) => item.isRefined);

        let html = '';

        if (showAll) {
          const isAllActive = !hasRefinement;
          html += `
                        <button type="button" data-value="" class="filter-tab ${isAllActive ? 'filter-tab--active' : ''}">
                          ${allLabel}
                        </button>
                    `;
        }

        items.forEach((item) => {
          html += `
                        <button type="button" data-value="${item.value}" class="filter-tab ${item.isRefined ? 'filter-tab--active' : ''}">
                          ${item.label}
                        </button>
                    `;
        });

        wrapper.innerHTML = html;
      };

      return connectMenu(renderMenuTabs)({ attribute, ...options });
    }

    const widgetOptions = {
      cssClasses: {
        root: '',
        list: 'space-y-1',
        item: 'py-1',
        selectedItem: 'font-medium',
        link: 'text-sm text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-gray-100',
        count: 'ml-2 text-xs text-gray-600 dark:text-gray-400',
      },
      ...options,
    };

    if (usePanel) {
      return createPanel(menu, {
        container,
        attribute,
        label,
        widgetOptions,
      });
    }

    return menu({
      container,
      attribute,
      ...widgetOptions,
    });
  },

  searchBox: (container, _, options) => {
    const debounceMs = parseInt(container.dataset.debounce, 10) || 350;
    const minLength = parseInt(container.dataset.minLength, 10) || 2;

    return searchBox({
      container,
      placeholder: container.dataset.placeholder || texts.searchPlaceholder,
      queryHook: createFilteredQueryHook(debounceMs, minLength),
      cssClasses: {
        root: '',
        form: 'relative',
        input:
          'block w-full rounded-md border-0 py-1.5 pl-3 pr-10 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 placeholder:text-gray-600 dark:placeholder:text-gray-400 shadow-xs ring-1 ring-inset ring-gray-300 dark:ring-gray-600 focus:ring-primary focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6',
        submit: 'absolute right-3 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400',
        reset:
          'absolute right-10 top-1/2 -translate-y-1/2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
        loadingIndicator: 'absolute right-3 top-1/2 -translate-y-1/2',
      },
      templates: {
        submit: `
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                    </svg>
                `,
        reset: `
                    <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                `,
      },
      ...options,
    });
  },

  clearRefinements: (container, _, options) => {
    if (container.dataset.excludedAttributes) {
      try {
        options.excludedAttributes = JSON.parse(
          container.dataset.excludedAttributes,
        );
      } catch (e) {
        options.excludedAttributes =
          container.dataset.excludedAttributes.split(',');
      }
    }

    return clearRefinements({
      container,
      templates: clearRefinementsTemplate,
      cssClasses: {
        root: '',
        button:
          'mb-6 bg-black dark:bg-gray-700 px-4 py-2 text-sm text-white clip-corner-sm hover:bg-gray-800 dark:hover:bg-gray-600 cursor-pointer',
        disabledButton: 'hidden',
      },
      ...options,
    });
  },

  sortBy: (container, _, options) => {
    return sortBy({
      container,
      cssClasses: {
        root: '',
        select:
          'text-sm bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100 border border-gray-300 dark:border-gray-600 rounded-md pl-3 pr-8 py-2 focus:ring-primary focus:ring-2 focus:ring-inset focus:outline-none',
      },
      ...options,
    });
  },

  stats: (container, _, options) => {
    return stats({
      container,
      templates: {
        text: statsTemplate,
      },
      cssClasses: {
        root: '',
        text: '',
      },
      ...options,
    });
  },
};

export function createWidgetInstances(containers) {
  algoliaLog(
    '[createWidgetInstances] Processing containers:',
    containers.length,
  );
  return containers
    .map((container) => {
      const widgetType = container.dataset.widgetType;
      const attribute = container.dataset.attribute;

      algoliaLog('[createWidgetInstances] Widget:', { widgetType, attribute });

      const widgetFactory = widgetConfigs[widgetType];

      if (!widgetFactory) {
        console.warn(`Unknown widget type: ${widgetType}`);
        return null;
      }

      const options = parseWidgetOptions(container);
      return widgetFactory(container, attribute, options);
    })
    .filter(Boolean);
}

function parseWidgetOptions(container) {
  const options = {};

  if (container.dataset.limit) {
    options.limit = parseInt(container.dataset.limit, 10);
  }

  if (container.dataset.showMore === 'true') {
    options.showMore = true;
    options.showMoreLimit = parseInt(container.dataset.showMoreLimit, 10) || 20;
  }

  if (container.dataset.searchable === 'true') {
    options.searchable = true;
    options.searchablePlaceholder =
      container.dataset.searchablePlaceholder || texts.searchPlaceholder;
  }

  if (container.dataset.sortBy) {
    options.sortBy = container.dataset.sortBy.split(',');
  }

  if (container.dataset.items) {
    try {
      options.items = JSON.parse(container.dataset.items);
    } catch (e) {
      console.warn('Error parsing items:', e);
    }
  }

  return options;
}
